import { formatDurationAmount, formatThresholdAmount } from '../components/shared/severity-sensitivity/severity-sensitivity.util';
import { SignalTextPart } from '../models';
import {
  ACTIVITY_SUMMARY_URL, HOURS_TRACKED_URL, joinWithAnd, UNUSUAL_ACTIVITY_URL
} from './ask-ai-signal-body';

/** Signal.key for a person card (see AskAiService.personCardToSignal) — deliberately matches no
 * real alertId, so it falls through to the default body template and is exempt from the
 * sensitivity-notch filter (a person card spans multiple alert types, so no single notch applies). */
export const PERSON_CARD_SIGNAL_KEY = 'person_card';

/**
 * AA-214 — Needs you combined person signal cards. USER cards only (group/team cards are a
 * separate feature); pure string/data logic, no Angular dependency, so it's unit-testable on its
 * own. attendance_issues is out of scope — never add it to PERSON_SIGNAL_TYPES or any switch here.
 */

export type PersonSignalType = 'idle_time' | 'heavy_hours' | 'unusual_activity' | 'manual_time' | 'mobile_time';

/** Fixed scope + title/body tie-break order (Section "Scope"). */
export const PERSON_SIGNAL_TYPES: readonly PersonSignalType[] = [
  'idle_time', 'heavy_hours', 'unusual_activity', 'manual_time', 'mobile_time',
];

export type CardSeverity = 'info' | 'warning' | 'critical';

export const SEVERITY_RANK: Record<CardSeverity, number> = { info: 0, warning: 1, critical: 2 };
const SEVERITY_BY_RANK: CardSeverity[] = ['info', 'warning', 'critical'];

function capSeverity(band: CardSeverity, capAt: CardSeverity): CardSeverity {
  return SEVERITY_RANK[band] > SEVERITY_RANK[capAt] ? capAt : band;
}

function escalateSeverity(band: CardSeverity): CardSeverity {
  return SEVERITY_BY_RANK[Math.min(SEVERITY_RANK[band] + 1, SEVERITY_RANK.critical)];
}

/** Base severity band from `peak` vs `threshold`, using x/y (SeveritySensitivityConfig, models.ts)
 * as the info/warning/critical distances — the generic band math the spec keeps untouched by
 * escalation. */
export function classifyBaseBand(peak: number, threshold: number, x: number, y: number,
  direction: 'above' | 'below' = 'above'): CardSeverity {
  if (direction === 'below') {
    if (peak <= threshold - y) return 'critical';
    if (peak <= threshold - x) return 'warning';
    return 'info';
  }
  if (peak >= threshold + y) return 'critical';
  if (peak >= threshold + x) return 'warning';
  return 'info';
}

/**
 * Full severity rule: base band, then at most one band of movement from a single mutually-
 * exclusive trigger on N — never both, never averaged, never a de-escalation.
 *   - N === 1: cap at Warning (a single flagged day never reaches Critical).
 *   - else N/D >= 0.5: escalate one band, capped at Critical.
 *   - else: base band stands.
 * Global across all five signal types — no per-type persistence threshold.
 */
export function severityForSignal(peak: number, threshold: number, x: number, y: number, n: number, d: number,
  direction: 'above' | 'below' = 'above'): CardSeverity {
  const base = classifyBaseBand(peak, threshold, x, y, direction);
  if (n === 1) return capSeverity(base, 'warning');
  if (d > 0 && n / d >= 0.5) return escalateSeverity(base);
  return base;
}

/** Card-level badge = max severity across the user's fired signals. */
export function cardBadgeSeverity(severities: CardSeverity[]): CardSeverity {
  return severities.reduce<CardSeverity>((max, s) => (SEVERITY_RANK[s] > SEVERITY_RANK[max] ? s : max), 'info');
}

/** One fired signal type for one user's card, per the AA-214 data model. `peak`/`threshold` are in
 * the signal's native unit: a percent number for idle_time, decimal hours for the other four. */
export interface FiredSignal {
  type: PersonSignalType;
  n: number; // flagged day count in range
  d: number; // expected work days in range for this user
  threshold: number; // T
  peak: number; // highest value recorded in range
  peakDay: string; // weekday name the peak fell on, e.g. 'Wed'
  direction?: 'above' | 'below'; // default 'above' — every current signal type is above-threshold
  x: number; // base-band distance for Warning
  y: number; // base-band distance for Critical
  // Every backend row behind this fired signal (not just the peak) — dismissing only the peak's id
  // would leave the rest active server-side, so the type would fire again next fetch.
  signalIds?: string[];
  // Whether peakDay is a real date or an estimated placeholder (range-aggregate fallback) — only a
  // real peakDay gets a citation link; linking to a guessed day would be worse than no link at all.
  peakDayIsReal?: boolean;
  /** Backend-provided report URL. When set, citation/evidence links must use it — never overwrite
   * with a client-built fallback (see resolveSignalLink). */
  deepLink?: string;
}

function pluralize(n: number, word: string): string {
  return n === 1 ? word : `${word}s`;
}

/** Duration-native signals format T/peak as "10h 45m" (formatDurationAmount) but drop a trailing
 * "00m" — the worked example's threshold labels read "8h"/"1h", never "8h 00m"/"1h 00m". */
function formatDurationLabel(hours: number): string {
  return formatDurationAmount(hours).replace(/ 00m$/, '');
}

interface SignalTemplateConfig {
  label: string; // sentence-case display label, exact per the spec's fixed strings
  shortLabel: string; // compact label for the daily chart's per-bar tooltip (see bodyToTextParts' sibling below)
  formatValue: (v: number) => string;
  thresholdPhrase: (formattedThreshold: string) => string;
}

const TEMPLATE_CONFIG: Record<PersonSignalType, SignalTemplateConfig> = {
  idle_time: {
    label: 'idle time',
    shortLabel: 'Idle',
    formatValue: v => formatThresholdAmount(v, '%'),
    thresholdPhrase: t => `${t} idle limit`,
  },
  heavy_hours: {
    label: 'heavy hours',
    shortLabel: 'Heavy',
    formatValue: formatDurationLabel,
    thresholdPhrase: t => `${t} daily limit`,
  },
  unusual_activity: {
    label: 'unusual activity',
    shortLabel: 'Unusual',
    formatValue: formatDurationLabel,
    thresholdPhrase: t => `${t} unusual activity limit`,
  },
  manual_time: {
    label: 'manual time added',
    shortLabel: 'Manual',
    formatValue: formatDurationLabel,
    thresholdPhrase: t => `${t} daily manual time limit`,
  },
  mobile_time: {
    label: 'mobile time added',
    shortLabel: 'Mobile',
    formatValue: formatDurationLabel,
    thresholdPhrase: t => `${t} daily mobile time limit`,
  },
};

/** Sentence-case display label for one signal type, e.g. 'idle time', 'heavy hours' — the same
 * fixed strings the title/body use, for any caller that needs just the label (e.g. the brief's
 * headline summary across person cards). */
export function personSignalTypeLabel(type: PersonSignalType): string {
  return TEMPLATE_CONFIG[type].label;
}

/** Compact per-type label for the daily chart's bar tooltips, e.g. 'Idle', 'Heavy hours' -> 'Heavy'. */
export function personSignalTypeShortLabel(type: PersonSignalType): string {
  return TEMPLATE_CONFIG[type].shortLabel;
}

/** Formats one native-unit value (a percent for idle_time, decimal hours for the other four) the
 * same way the body sentence's threshold/peak numbers already are — for any caller (e.g. the daily
 * chart's bar tooltips) that needs the same formatting outside a FiredSignal's own threshold/peak. */
export function formatPersonSignalValue(type: PersonSignalType, value: number): string {
  return TEMPLATE_CONFIG[type].formatValue(value);
}

// AA-247 aliases — the per-user week-matrix detail module (person-week-evidence-detail.ts) and the
// person-week preview adapter were written against these earlier names. Kept as thin aliases so
// Iulian's AA-225/AA-247 code and main's grouped-signal code share one implementation.
export const signalTypeLabel = personSignalTypeLabel;
export const formatSignalValue = formatPersonSignalValue;

// ---- Title ----------------------------------------------------------------------------------

const TITLE_MAX_CHARS = 67;
const NAME_MAX_CHARS = 28;

/** Cuts at 27 chars + a trailing ellipsis (28 chars total) once `name` exceeds the 28-char cap. */
export function truncateUserName(name: string): string {
  if (name.length <= NAME_MAX_CHARS) return name;
  return `${name.slice(0, NAME_MAX_CHARS - 1)}…`;
}

/**
 * `{userName}: {signal list}`, hard-capped at 67 characters total. The signal list is fired-type
 * labels in `orderedTypes` order (already severity desc / N desc / fixed-order tie-break — see
 * `orderSignalsForCard`); if the full joined list doesn't fit the remaining budget, it's cut down
 * to the largest number of whole labels that fit, plus a trailing " and {N} more" pseudo-item
 * (never a mid-word cut, since only whole labels are ever included).
 */
export function buildCardTitle(userName: string, orderedTypes: PersonSignalType[]): string {
  const truncatedName = truncateUserName(userName);
  const prefix = `${truncatedName}: `;
  const labels = orderedTypes.map(t => TEMPLATE_CONFIG[t].label);
  const full = joinWithAnd(labels);
  if ((prefix + full).length <= TITLE_MAX_CHARS) return prefix + full;

  const budget = TITLE_MAX_CHARS - prefix.length;
  for (let shown = labels.length - 1; shown >= 1; shown--) {
    const hidden = labels.length - shown;
    const candidate = joinWithAnd([...labels.slice(0, shown), `${hidden} more`]);
    if (candidate.length <= budget) return prefix + candidate;
  }
  // Degenerate case (an extremely long name eating the whole budget) — no label survives; the
  // name plus colon is still the truest thing we can render.
  return prefix.trimEnd();
}

/** The question "Analyze" sends for a person card — every fired signal type, so the AI Agent's
 * answer covers the whole card rather than just its headline. Unlike buildCardTitle, this never
 * truncates: it's a real question going to the model, not text going onto a fixed-width label. */
export function buildPersonCardAskPrompt(userName: string, orderedTypes: PersonSignalType[]): string {
  const labels = orderedTypes.map(t => TEMPLATE_CONFIG[t].label);
  return `What's driving ${joinWithAnd(labels)} for ${userName}?`;
}

// ---- Ordering ---------------------------------------------------------------------------------

/** Severity desc, then N desc, then the fixed PERSON_SIGNAL_TYPES order as a final, deterministic
 * tie-break — shared by the title and the body, so both always list signals in the same order. */
export function orderSignalsForCard(signals: FiredSignal[], severities: Record<PersonSignalType, CardSeverity>): FiredSignal[] {
  const priorityIndex = (t: PersonSignalType) => PERSON_SIGNAL_TYPES.indexOf(t);
  return [...signals].sort((a, b) => {
    const sevDiff = SEVERITY_RANK[severities[b.type]] - SEVERITY_RANK[severities[a.type]];
    if (sevDiff !== 0) return sevDiff;
    const nDiff = b.n - a.n;
    if (nDiff !== 0) return nDiff;
    return priorityIndex(a.type) - priorityIndex(b.type);
  });
}

// ---- Body sentences -------------------------------------------------------------------------

/** Weekday order the closed range runs Monday through, used only to resolve a `peakDay` name to
 * an actual date for citation links (see `buildSignalLink`). */
export const WEEKDAY_ORDER = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

// UTC-based arithmetic throughout — weekStart is treated as a UTC midnight instant, so the
// resolved date doesn't drift a day depending on the runner's local timezone.
function resolvePeakDate(weekStart: Date | undefined, peakDay: string): Date | null {
  if (!weekStart) return null;
  const offset = WEEKDAY_ORDER.indexOf(peakDay);
  if (offset < 0) return null;
  const date = new Date(weekStart);
  date.setUTCDate(date.getUTCDate() + offset);
  return date;
}

/** Deep-link builder — an interface so rendering never depends on link availability (null/omitted
 * always renders plain text). Keyed by signalType, not just the report, so types sharing a report
 * still resolve independent links (no dedup). */
export type SignalLinkBuilder = (signalType: PersonSignalType, userId: string, date: Date) => string | null;

/** Default stub — always renders plain text with no link. */
export const NULL_SIGNAL_LINK_BUILDER: SignalLinkBuilder = () => null;

/**
 * Real, client-side deep link — reuses the report pages' existing route + query-param convention
 * (DashboardService.createParamsFromQuery): `${route};from=YYYY-MM-DD;to=YYYY-MM-DD;userIds=<id>`,
 * one day's range. No backend call, no Router injection (opens in a new tab as a plain string).
 *
 * Report per type follows the citation-links spec, not ask-ai-signal-body.ts's routing: manual_time/
 * mobile_time go to Activity Summary here (not Edit Time) — deliberate divergence, not an oversight.
 */
const PERSON_SIGNAL_REPORT_ROUTE: Record<PersonSignalType, string> = {
  idle_time: ACTIVITY_SUMMARY_URL,
  heavy_hours: HOURS_TRACKED_URL,
  unusual_activity: UNUSUAL_ACTIVITY_URL,
  manual_time: ACTIVITY_SUMMARY_URL,
  mobile_time: ACTIVITY_SUMMARY_URL,
};

export const buildPersonCardSignalLink: SignalLinkBuilder = (signalType, userId, date) => {
  const route = PERSON_SIGNAL_REPORT_ROUTE[signalType];
  const day = date.toISOString().slice(0, 10);
  return `${route};from=${day};to=${day};userIds=${encodeURIComponent(userId)}`;
};

/**
 * Gateway sometimes ships `/approvals` for idle/manual/mobile — those types have no Approvals
 * report in this product surface; citation chips must use Activity Summary instead (see
 * PERSON_SIGNAL_REPORT_ROUTE). Treat those deepLinks as absent so the client fallback wins.
 */
const REJECT_APPROVALS_DEEP_LINK = new Set<PersonSignalType>([
  'idle_time', 'manual_time', 'mobile_time',
]);

function isUsableDeepLink(type: PersonSignalType | undefined, deepLink: string): boolean {
  if (!type || !REJECT_APPROVALS_DEEP_LINK.has(type)) return true;
  return !/^\/approvals(?:;|\?|$)/i.test(deepLink);
}

/**
 * Prefer the gateway's `deepLink` when present and usable; only fall back to a client-built URL
 * otherwise. Empty/whitespace deepLinks are treated as absent so a blank response field cannot
 * blank the chip.
 */
export function resolveSignalLink(
  sig: Pick<FiredSignal, 'deepLink'> & { type?: PersonSignalType },
  fallback: string | null | undefined,
): string | null {
  const fromResponse = sig.deepLink?.trim();
  if (fromResponse && isUsableDeepLink(sig.type, fromResponse)) return fromResponse;
  const built = fallback?.trim();
  return built || null;
}

export interface RenderedLine {
  text: string;
  linkTarget: string | null;
  // Exact substring of `text` the link wraps — always present (non-null) whenever linkTarget is
  // non-null, so callers can find-and-wrap it without reformatting the sentence.
  linkSubstring: string | null;
}

function fullSentenceLine(sig: FiredSignal, userId: string, weekStart: Date | undefined, buildLink: SignalLinkBuilder): RenderedLine {
  const cfg = TEMPLATE_CONFIG[sig.type];
  const tLabel = cfg.formatValue(sig.threshold);
  const peakLabel = cfg.formatValue(sig.peak);
  const dayWord = pluralize(sig.d, 'day');
  const verb = sig.n === 1 ? 'at' : 'peaking at';
  const linkSubstring = `${peakLabel} on ${sig.peakDay}`;
  const text = `${sig.n} of ${sig.d} ${dayWord} above your ${cfg.thresholdPhrase(tLabel)}, ${verb} ${linkSubstring}.`;

  const date = sig.peakDayIsReal !== false ? resolvePeakDate(weekStart, sig.peakDay) : null;
  const fallback = date ? buildLink(sig.type, userId, date) : null;
  // Gateway deepLink wins even when peakDay is an estimate — the backend already resolved the URL.
  const linkTarget = resolveSignalLink(sig, fallback);
  return { text, linkTarget, linkSubstring: linkTarget ? linkSubstring : null };
}

export interface RenderedBody {
  lines: RenderedLine[]; // one full sentence per fired signal, in orderSignalsForCard order
  text: string; // lines joined by "\n" — the card's whole body, always showing every fired signal
}

/**
 * Renders every fired signal as its own full sentence, in severity/N order — no truncation, no
 * compact tail, no "Show all N signals" toggle: every signal type a card fires for is always
 * shown in full. `userId`/`weekStart` resolve citation links; omit `weekStart` (or pass no
 * `buildLink`) to render plain, link-free text.
 */
export function renderPersonCardBody(orderedSignals: FiredSignal[], userId: string, weekStart?: Date,
  buildLink: SignalLinkBuilder = NULL_SIGNAL_LINK_BUILDER): RenderedBody {
  const lines = orderedSignals.map(s => fullSentenceLine(s, userId, weekStart, buildLink));
  return { lines, text: lines.map(l => l.text).join('\n') };
}

/** Splits one rendered line's plain text around its own linkSubstring into the SignalTextPart[]
 * shape every Signal body already renders with (app-ask-ai-text-parts), so the citation chip (if
 * any) lands in place. */
export function lineToTextParts(line: RenderedLine): SignalTextPart[] {
  if (!line.linkTarget || !line.linkSubstring) return [{ text: line.text }];
  const idx = line.text.indexOf(line.linkSubstring);
  if (idx < 0) return [{ text: line.text }];
  const before = line.text.slice(0, idx);
  const after = line.text.slice(idx + line.linkSubstring.length);
  const parts: SignalTextPart[] = [];
  if (before) parts.push({ text: before });
  parts.push({ link: line.linkSubstring, url: line.linkTarget });
  if (after) parts.push({ text: after });
  return parts;
}

/** A RenderedBody's lines as one flat SignalTextPart[], joined with {break:true} between each
 * sentence — the same paragraph-break convention every other multi-line Signal body already uses
 * (see SignalTextPart.break), so this renders through the ordinary single app-ask-ai-text-parts
 * binding instead of one `*ngFor` div per line. */
export function bodyToTextParts(body: RenderedBody): SignalTextPart[] {
  const parts: SignalTextPart[] = [];
  body.lines.forEach((line, i) => {
    if (i > 0) parts.push({ break: true });
    parts.push(...lineToTextParts(line));
  });
  return parts;
}

// ---- Grouping ---------------------------------------------------------------------------------

export interface PersonCard {
  userId: string;
  userName: string;
  orderedTypes: PersonSignalType[];
  severities: Record<PersonSignalType, CardSeverity>;
  badge: CardSeverity;
  title: string;
  body: RenderedBody;
  signalCount: number; // how many signal types fired — every one is always rendered in the body
  // The Signal shape has one threshold. Carry the lead signal (worst, first in orderedTypes) so
  // the chart and the suggested button use that limit, not a placeholder 0.
  leadThreshold: number;
  leadUnit: string;
  leadDirection: 'above' | 'below';
  leadPeak: number;
  // Every fired signal's own FiredSignal.signalIds, flattened across types, blank ones dropped —
  // same "never send an empty string to the dismiss API" posture as buildGroupedSignal's
  // Signal.signalIds.
  signalIds: string[];
}

/**
 * Builds one user's Needs you card from their fired signals. Returns null when nothing fired.
 *
 * `precomputedSeverities` lets a caller override the computed severity with the backend's real
 * classification — needed for the range-aggregate fallback, whose n is pinned to 1 and would
 * otherwise always cap the result at 'warning' even when the backend says 'critical'.
 */
export function buildPersonCard(userId: string, userName: string, signals: FiredSignal[], weekStart?: Date,
  buildLink: SignalLinkBuilder = NULL_SIGNAL_LINK_BUILDER,
  precomputedSeverities?: Partial<Record<PersonSignalType, CardSeverity>>): PersonCard | null {
  if (signals.length === 0) return null;

  const severities = {} as Record<PersonSignalType, CardSeverity>;
  for (const sig of signals) {
    severities[sig.type] = precomputedSeverities?.[sig.type]
      ?? severityForSignal(sig.peak, sig.threshold, sig.x, sig.y, sig.n, sig.d, sig.direction ?? 'above');
  }

  const orderedSignals = orderSignalsForCard(signals, severities);
  const orderedTypes = orderedSignals.map(s => s.type);
  const badge = cardBadgeSeverity(Object.values(severities));
  const title = buildCardTitle(userName, orderedTypes);
  const body = renderPersonCardBody(orderedSignals, userId, weekStart, buildLink);
  const signalIds = signals.flatMap(s => s.signalIds ?? []).filter(id => id.trim().length > 0);
  const lead = orderedSignals[0];

  return {
    userId, userName, orderedTypes, severities, badge, title, body, signalCount: signals.length, signalIds,
    leadThreshold: lead.threshold,
    leadUnit: lead.type === 'idle_time' ? '%' : 'h',
    leadDirection: lead.direction ?? 'above',
    leadPeak: lead.peak,
  };
}
